import { sanitizeEditorClipboardHTML } from "@/tiptap/core/editor-clipboard"
import { parseInertHTML } from "@/tiptap/core/inert-html"

export function normalizeInitialContent(content) {
  if (typeof content !== "string") return "<p></p>"

  const trimmedContent = content.trim()
  return trimmedContent.length > 0 ? trimmedContent : "<p></p>"
}

function stripBreaksBeforeNestedLists(html) {
  if (typeof document === "undefined") return html

  const container = parseInertHTML(html)

  container.querySelectorAll("li > ol, li > ul").forEach((list) => {
    let sibling = list.previousSibling

    while (sibling && sibling.nodeType === Node.TEXT_NODE && sibling.textContent?.trim() === "") {
      const previousSibling = sibling.previousSibling
      sibling.remove()
      sibling = previousSibling
    }

    while (sibling instanceof HTMLBRElement) {
      const previousSibling = sibling.previousSibling
      sibling.remove()
      sibling = previousSibling

      while (sibling && sibling.nodeType === Node.TEXT_NODE && sibling.textContent?.trim() === "") {
        const previousSibling = sibling.previousSibling
        sibling.remove()
        sibling = previousSibling
      }
    }
  })

  return container.innerHTML
}

function normalizeTaskListForEditing(html) {
  if (typeof document === "undefined") return html

  const container = parseInertHTML(html)

  container
    .querySelectorAll('ul[data-type="taskList"] li[data-type="taskItem"]')
    .forEach((taskItem) => {
      taskItem.querySelectorAll(":scope > label").forEach((label) => {
        if (label.querySelector('input[type="checkbox"]')) label.remove()
      })

      taskItem.querySelectorAll(":scope > div").forEach((contentWrapper) => {
        contentWrapper.replaceWith(...Array.from(contentWrapper.childNodes))
      })
    })

  return container.innerHTML
}

function normalizeLegacyCalloutsForEditing(html) {
  if (typeof document === "undefined") return html

  const container = parseInertHTML(html)
  const inertDocument = container.ownerDocument

  container.querySelectorAll("p.callout").forEach((callout) => {
    const wrapper = inertDocument.createElement("div")

    Array.from(callout.attributes).forEach((attribute) => {
      wrapper.setAttribute(attribute.name, attribute.value)
    })

    const paragraph = inertDocument.createElement("p")
    paragraph.replaceChildren(...Array.from(callout.childNodes))
    wrapper.appendChild(paragraph)
    callout.replaceWith(wrapper)
  })

  return container.innerHTML
}

function unwrapCodeBlockChrome(html) {
  if (typeof document === "undefined") return html

  const container = parseInertHTML(html)

  container.querySelectorAll(".code-block-wrapper").forEach((wrapper) => {
    const pre = wrapper.querySelector(":scope > pre")
    if (pre) {
      wrapper.replaceWith(pre)
      return
    }

    wrapper.replaceWith(...Array.from(wrapper.childNodes))
  })

  container.querySelectorAll("select.code-block-language-selector").forEach((select) => {
    select.remove()
  })

  return container.innerHTML
}

function unwrapTinyMceMediaPreviewObjects(html) {
  if (typeof document === "undefined") return html

  const container = parseInertHTML(html)

  container
    .querySelectorAll(
      'span[data-mce-object="audio"], span[data-mce-object="video"], span[data-mce-object="iframe"]'
    )
    .forEach((wrapper) => {
      const mediaTag = wrapper.getAttribute("data-mce-object")
      const mediaElement = mediaTag ? wrapper.querySelector(mediaTag) : null

      if (!mediaElement) return

      wrapper.replaceWith(mediaElement.cloneNode(true))
    })

  return container.innerHTML
}

function normalizeEmojiFallbackImagesForEditing(html) {
  if (typeof document === "undefined") return html

  const container = parseInertHTML(html)
  const inertDocument = container.ownerDocument

  container
    .querySelectorAll('img[src*="emoji-datasource-"][alt$=" emoji"]')
    .forEach((image) => {
      if (image.closest('[data-type="emoji"]')) return

      const emojiName = image.getAttribute("alt")?.replace(/\s+emoji$/, "")
      if (!emojiName) return

      const wrapper = inertDocument.createElement("span")
      wrapper.setAttribute("data-name", emojiName)
      wrapper.setAttribute("data-type", "emoji")
      wrapper.setAttribute("contenteditable", "false")

      const emojiImage = image.cloneNode(true)
      emojiImage.setAttribute("draggable", "false")
      emojiImage.setAttribute("loading", "lazy")
      emojiImage.setAttribute("align", "absmiddle")
      emojiImage.setAttribute("alt", `${emojiName} emoji`)

      wrapper.appendChild(emojiImage)
      image.replaceWith(wrapper)
    })

  return container.innerHTML
}

export function deserializeEditorHTML(content) {
  const normalizedContent = normalizeInitialContent(content)
  const editorChromeSanitizedContent = sanitizeEditorClipboardHTML(normalizedContent)
  const strippedContent = stripBreaksBeforeNestedLists(editorChromeSanitizedContent)
  const taskListNormalizedContent = normalizeTaskListForEditing(strippedContent)
  const calloutNormalizedContent = normalizeLegacyCalloutsForEditing(taskListNormalizedContent)
  const mediaNormalizedContent = unwrapTinyMceMediaPreviewObjects(calloutNormalizedContent)
  const codeBlockNormalizedContent = unwrapCodeBlockChrome(mediaNormalizedContent)
  return normalizeEmojiFallbackImagesForEditing(codeBlockNormalizedContent)
}

function preserveLiveTableStyles(html, editorDom) {
  if (typeof document === "undefined" || !editorDom) return html

  const liveTables = Array.from(editorDom.querySelectorAll("table"))
  if (liveTables.length === 0) return html

  const container = parseInertHTML(html)

  container.querySelectorAll("table").forEach((table, index) => {
    const liveStyle = liveTables[index]?.getAttribute("style")

    if (liveStyle) {
      table.setAttribute("style", liveStyle)
    }
  })

  return container.innerHTML
}

export function serializeEditorHTML(editor) {
  const html = editor?.getHTML?.() || ""
  return preserveLiveTableStyles(html, editor?.view?.dom)
}
